"use client";

import { useFormatter, useTranslations } from "next-intl";
import { Clock, Mic, MicOff } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/ui/data-display/avatar";
import { useLfgLftApplicationsIncoming, useLfgLftApplicationsOutgoing, useLfgLftPost } from "@/hooks/useLfgLft";
import type { LfgChatPayload } from "../model/chat-card";
import { cn } from "@/shared/lib/utils";
import { isLfgRole, toLfgRank } from "../model/board-filters";
import { LfgRoleIcon, RankLabel } from "./board-card-parts";
import { LfgChatCardActions } from "./lfg-chat-card-actions";

/** Палитра фрейма: в ленте чата — токены чата, в модалке доски — токены доски. */
const TONE = {
  chat: {
    frame: "border-chat-border bg-chat-card text-chat-fg",
    muted: "text-chat-muted-fg",
    accent: "text-chat-accent-ink",
    badge: "bg-chat-accent-soft text-chat-accent-ink",
    chip: "border-chat-border bg-chat-panel",
    panel: "bg-chat-panel",
    free: "border-chat-bubble-own-border bg-chat-accent-soft text-chat-accent-ink",
  },
  board: {
    frame: "border-border bg-muted/30 text-foreground",
    muted: "text-muted-foreground",
    accent: "text-highlight",
    badge: "bg-highlight-soft text-highlight",
    chip: "border-border bg-muted/40",
    panel: "bg-muted/40",
    free: "border-highlight-border bg-highlight-soft/50 text-highlight",
  },
} as const;

export interface LfgChatCardProps {
  /** Снимок объявления; актуальное подтягивается по id — правки автора видны сразу. */
  post: LfgChatPayload;
  /** Своё сообщение в ленте: «Ждём ответа» вместо перехода на доску. */
  own?: boolean;
  /** Превью в модалке: без нижней строки действий. */
  preview?: boolean;
  /** Свои действия вместо «Откликнуться»/«Открыть» (чат: «Вступить»/«Отклонить» у приглашения капитана). */
  actions?: React.ReactNode;
  tone?: keyof typeof TONE;
  /** Без состава команды (модалка на доске — как в макете). */
  compact?: boolean;
  /** Модалка из «Заявок»: во фрейме — собеседник и текст заявки, а не автор объявления. */
  header?: { name: string; avatarUrl?: string | null; team: boolean };
  description?: string | null;
  meta?: string;
}

/** Фрейм объявления LFG/LFT (макеты чата и доски): один компонент для ленты, превью и модалки отклика. */
export function LfgChatCard({ post: snapshot, own, preview, actions, tone = "chat", compact, ...overrides }: LfgChatCardProps) {
  let { header, description, meta } = overrides;
  const t = useTranslations("LfgBoard");
  const format = useFormatter();
  const live = useLfgLftPost(snapshot.id);
  const post = live.data ?? snapshot;
  // Фрейм и «без кнопок» живут только в снимке сообщения: живое объявление их не несёт.
  const { frame, noActions } = snapshot;
  // Итог у отправителя и статус отклика — по заявкам доски (живые, общий кэш React Query).
  const track = !preview && (own || !!frame);
  const outgoing = useLfgLftApplicationsOutgoing(track);
  const incoming = useLfgLftApplicationsIncoming(track && !!frame);
  const apps = [...(outgoing.data ?? []), ...(incoming.data ?? [])];
  const app = frame ? apps.find((a) => a.id === frame.applicationId) : (outgoing.data ?? []).find((a) => a.postId === snapshot.id);
  const appStatus = app?.status ?? frame?.applicationStatus;
  const closed = live.isSuccess && (!live.data || live.data.status !== "Active");
  const c = TONE[tone];
  if (frame && !header) header = { name: frame.name, avatarUrl: frame.avatarUrl, team: frame.isTeam };
  if (frame && description === undefined) description = frame.description ?? undefined;
  if (frame && meta === undefined && appStatus) meta = t(`chatCard.application.${appStatus}`);

  const postIsTeam = post.type === "Lft";
  // Шапка — собеседник (в «Заявках» он может быть командой), а чипы — всегда про само объявление.
  const isTeam = header ? header.team : postIsTeam;
  const riot = post.playerSurface?.primaryRiot;
  const name = header?.name ?? (post.type === "Lft" ? post.author?.displayName : post.playerSurface?.displayName ?? post.author?.displayName) ?? "—";
  const avatar = header ? header.avatarUrl : post.type === "Lft" ? post.author?.avatarUrl : post.playerSurface?.avatarUrl ?? post.author?.avatarUrl;
  const tag = header ? null : post.type === "Lft" ? post.author?.tag : riot?.tagLine;
  const roles = (post.roles ?? [post.role]).filter(isLfgRole);
  const slots = post.rosterSlots ?? [];
  const filled = slots.filter((s) => s.slotStatus === "Filled").length;
  const text = description === undefined ? post.description : description;
  const published = t("chatCard.published", { date: format.dateTime(new Date(post.createdAt), { dateStyle: "short" }) });
  const metaLine = meta ?? (post.type === "Lft" && slots.length > 0 ? `${t("chatCard.slots", { filled, total: slots.length })} · ${published}` : published);

  const body = (
    <>
      <span className="flex items-center gap-2.5">
        <Avatar className="size-8 flex-none">
          <AvatarImage src={avatar ?? undefined} alt="" />
          <AvatarFallback className="font-logo text-xs">{name.slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-1.5">
            <span className={cn("truncate font-logo text-sm", tone === "board" && c.accent)}>{name}</span>
            <span className={cn("flex-none text-xs", c.muted)}>{tag ? `#${tag}` : isTeam ? t("chatCard.teamTag") : null}</span>
          </span>
          <span className="mt-0.5 flex items-center gap-1.5">
            <span className={cn("inline-flex h-[18px] items-center rounded-[5px] px-1.5 text-[10px] font-bold", c.badge)}>
              {isTeam ? t("chatCard.badgeTeam") : t("chatCard.badgePlayer")}
            </span>
            <span className={cn("truncate text-[11.5px]", c.muted)}>{isTeam ? t("card.kindTeam") : t("card.kindPlayer")}</span>
          </span>
        </span>
      </span>

      {text && <span className="block whitespace-pre-wrap text-[13px] leading-normal">{text}</span>}

      <span className="flex flex-wrap gap-1.5">
        {!postIsTeam && riot && (
          <Chip className={c.chip}>
            <RankLabel rank={toLfgRank(riot.currentRank)} size={15} />
          </Chip>
        )}
        {postIsTeam && post.minRank && (
          <Chip className={c.chip}>
            <RankLabel rank={toLfgRank(post.minRank)} size={15} />+
          </Chip>
        )}
        {roles.slice(0, 3).map((r) => (
          <Chip key={r} className={c.chip}>
            <LfgRoleIcon role={r} size={14} />
            <span className="uppercase">{t(`role.${r}`)}</span>
          </Chip>
        ))}
        {post.mic === "With" && (
          <Chip className={c.chip}>
            <Mic aria-hidden className="size-3.5 text-score-win" />
            {t("chatCard.voiceYes")}
          </Chip>
        )}
        {post.mic === "Without" && (
          <Chip className={c.chip}>
            <MicOff aria-hidden className="size-3.5 text-destructive" />
            {t("chatCard.voiceNo")}
          </Chip>
        )}
      </span>

      {!compact && post.type === "Lft" && slots.length > 0 && (
        <span className={cn("flex flex-col gap-1.5 rounded-[10px] border p-2", c.chip)}>
          <span className="flex items-center justify-between gap-2 px-0.5">
            <span className="truncate font-logo text-[12.5px]">{post.author?.displayName}</span>
            <span className={cn("flex-none font-logo text-xs", c.accent)}>{t("card.rosterCount", { filled, total: slots.length })}</span>
          </span>
          {slots
            .filter((s) => s.slotStatus !== "Vacant")
            .map((s) => {
              const role = isLfgRole(s.lane) ? s.lane : null;
              const free = s.slotStatus !== "Filled";
              return (
                <span key={s.lane} className={cn("flex items-center gap-2 rounded-[9px] px-2 py-1.5 text-[12.5px]", free ? cn("border border-dashed", c.free) : c.panel)}>
                  <span className="min-w-0 flex-1 truncate font-semibold">{free ? t("chatCard.freeSlot") : (s.displayName ?? "—")}</span>
                  {!free && s.rank && (
                    <span className="inline-flex flex-none items-center gap-1 text-[11.5px]">
                      <RankLabel rank={toLfgRank(s.rank)} size={14} />
                    </span>
                  )}
                  {role && (
                    <span className="inline-flex flex-none items-center gap-1 text-[11px] uppercase">
                      <LfgRoleIcon role={role} size={13} />
                      {t(`role.${role}`)}
                    </span>
                  )}
                </span>
              );
            })}
        </span>
      )}

      <span className={cn("text-[11.5px]", c.muted)}>{closed && !meta ? t("chatCard.closed") : metaLine}</span>

      {!preview && own && (!closed || app) && (
        <span className={cn("flex items-center gap-1.5 rounded-[9px] px-2.5 py-1.5 text-xs", c.panel, appStatus === "Accepted" ? c.accent : c.muted)}>
          <Clock aria-hidden className="size-3.5" />
          {appStatus === "Accepted" ? t("chatCard.outcomeAccepted") : appStatus === "Declined" ? t("chatCard.outcomeDeclined") : t("chatCard.pending")}
        </span>
      )}
      {!preview && !own && !closed && !noActions && (actions ?? <LfgChatCardActions post={post} />)}
    </>
  );

  // Корень — не ссылка: внутри кнопки, а в чате по сообщению работает правый клик.
  return <div className={cn("flex flex-col gap-2.5 rounded-xl border p-3", c.frame, !preview && "my-1 min-w-[260px] max-w-[360px]")}>{body}</div>;
}

function Chip({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={cn("inline-flex h-[27px] items-center gap-1.5 whitespace-nowrap rounded-[7px] border px-2 font-logo text-[11.5px]", className)}>
      {children}
    </span>
  );
}
